import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Instance } from 'tippy.js';
import { DATE_FORMAT, dateToString, isDateValid, stringToDate } from '../utils';

export const CUSTOM_RANGE_LABEL = 'Custom Range';

export type DateRange = {
  dates: Date[];
  label?: string;
};

interface UseDateRangePickerParams {
  range: DateRange;
  minDate?: Date;
  maxDate?: Date;
  presets?: DateRange[];
  onChange(range: DateRange): void;
}

export const useDateRangePicker = ({
  range,
  minDate,
  maxDate,
  onChange,
  presets,
}: UseDateRangePickerParams) => {
  const [internalRange, setInternalRange] = useState(range);
  const [fromDate, setFromDate] = useState(dateToString(range.dates[0]));
  const [toDate, setToDate] = useState(dateToString(range.dates[1]));

  const submitRef = useRef<HTMLButtonElement>(null);

  const [calendarInstance, setCalendarInstance] = useState<Instance>();

  const [calendarKey, setCalendarKey] = useState(Date.now());
  const rerenderCalendar = useCallback(() => {
    // Force a re-render to prevent leaving the calendar in an incomplete selection state
    // https://github.com/wojtekmaj/react-calendar/issues/385
    setCalendarKey(Date.now());
  }, []);

  useEffect(() => {
    setInternalRange(range);
  }, [range]);

  const presetLabel = useMemo(() => {
    if (!presets) {
      return '';
    }

    if (!internalRange.label) {
      return CUSTOM_RANGE_LABEL;
    }

    return internalRange.label;
  }, [internalRange.label, presets]);

  const onInternalChange = useCallback(_range => {
    setInternalRange(_range);
    setFromDate(dateToString(_range.dates[0]));
    setToDate(dateToString(_range.dates[1]));

    if (submitRef.current) {
      submitRef.current.focus();
    }
  }, []);

  const onFromDateChange = useCallback(
    ({ target: { value } }) => {
      if (isDateValid({ date: value, minDate, maxDate })) {
        setInternalRange({
          ...internalRange,
          dates: [stringToDate(value), internalRange.dates[1]],
        });
      }

      setFromDate(value);
    },
    [internalRange, maxDate, minDate],
  );

  const onToDateChange = useCallback(
    ({ target: { value } }) => {
      if (isDateValid({ date: value, minDate, maxDate })) {
        setInternalRange({
          ...internalRange,
          dates: [internalRange.dates[0], stringToDate(value)],
        });
      }

      setToDate(value);
    },
    [internalRange, maxDate, minDate],
  );

  const reverseRange = useCallback(() => {
    setInternalRange({ ...internalRange, dates: [...internalRange.dates].reverse() });
    setFromDate(dateToString(internalRange.dates[1]));
    setToDate(dateToString(internalRange.dates[0]));
  }, [internalRange]);

  const fromDateValidation = useCallback(() => {
    if (!isDateValid({ date: fromDate, minDate, maxDate })) {
      const momentDate = moment(fromDate, DATE_FORMAT, true);

      // If user enters a valid date but one prior to minDate -> set it to minDate
      if (minDate && momentDate.isValid() && momentDate.isBefore(moment(minDate))) {
        setFromDate(dateToString(minDate));
        setInternalRange({
          ...internalRange,
          dates: [minDate, internalRange.dates[1]],
        });
      } else {
        setFromDate(dateToString(internalRange.dates[0]));
      }
    }

    if (moment(internalRange.dates[0]).isAfter(moment(internalRange.dates[1]))) {
      reverseRange();
    }
  }, [fromDate, internalRange, maxDate, minDate, reverseRange]);

  const toDateValidation = useCallback(() => {
    if (!isDateValid({ date: toDate, minDate, maxDate })) {
      setToDate(dateToString(internalRange.dates[1]));
    }

    if (moment(internalRange.dates[0]).isAfter(moment(internalRange.dates[1]))) {
      reverseRange();
    }
  }, [internalRange, maxDate, minDate, reverseRange, toDate]);

  const onInputKeydown = useCallback(
    (evt, inputType: 'fromDate' | 'toDate') => {
      if (evt.key === 'Enter') {
        if (inputType === 'fromDate') {
          fromDateValidation();
        } else {
          toDateValidation();
        }

        if (submitRef.current) {
          submitRef.current.focus();
        }
      }
    },
    [fromDateValidation, toDateValidation],
  );

  const commit = useCallback(() => {
    if (calendarInstance) {
      calendarInstance.hide();

      onChange({ dates: internalRange.dates, label: CUSTOM_RANGE_LABEL });
    }
  }, [calendarInstance, internalRange, onChange]);

  return {
    state: {
      internalRange,
      fromDate,
      toDate,
      presetLabel,
      calendarKey,
      setInternalRange,
      setFromDate,
      setToDate,
    },
    refs: {
      submitRef,
      calendarInstance,
      setCalendarInstance,
    },
    handlers: {
      onInternalChange,
      onFromDateChange,
      onToDateChange,
      onInputKeydown,
      commit,
    },
    helpers: {
      fromDateValidation,
      toDateValidation,
      reverseRange,
      rerenderCalendar,
    },
  };
};
